/**
 * DEFUSE S15 — typed property descriptor model (LEAF type module).
 *
 * The type vocabulary + descriptor factory for the per-entity-kind typed
 * property model. This is the single source both the panel-read (S16) and the
 * edit-write (S15 cutover) consume, replacing today's ad-hoc string-label
 * routing through `propertyTranslator` / `propertyCapabilities`.
 *
 * PLACEMENT (dep-graph, ПРИОРИТЕТ #2): the types live in this LEAF module — NOT
 * in the public `typedPropertyDescriptors.ts` entry — so the per-kind tables
 * (`descriptors/<family>.ts`) can import the `TypedPropertyDescriptor` type
 * without a back-edge to the core entry (which imports the barrel, which imports
 * the per-kind tables). Core re-exports every symbol here, so the public surface
 * still exposes the whole type model (S0.5 SLA intent). This mirrors the exact
 * cycle-break `geometryEntityTypes.ts` performed for `simpleGeometry.ts`.
 *
 * ПРИОРИТЕТ #1 — this artifact is the package-level CAD core typed model that
 * new property semantics must land in FIRST; preview/renderer/UI project FROM it
 * (S16), they do not define it. Nothing consumes it yet (Cat-B foundation).
 */

import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

/**
 * The property-editor widget a descriptor drives in the panel. One-to-one with
 * the editor-dispatch predicates the current `PropertyGrid` uses
 * (`isColorProperty` / `isLineweightProperty` / `isStyleProperty` / …), lifted
 * to a typed enum so S16 dispatches off the descriptor, not label string-matching.
 */
export type EditorKind =
  | "number"
  | "point-coord"
  | "color"
  | "layer-ref"
  | "linetype-ref"
  | "lineweight"
  | "enum"
  | "text"
  | "boolean"
  | "read-only-calc";

/**
 * How the field relates to the canonical typed `DrawingEntity`, so S14's
 * terminal side-channel deletion + S15/S16 can reason by-construction:
 *   - `editable-typed-field`   — resolves to a real (or computed-from-real)
 *                                typed field; S15 writes it back.
 *   - `read-only-calc`         — derived display value (Appendix B pureUiKeys:
 *                                Length/Area/Diameter/Measurement/Subtype) — no
 *                                independent typed home; recomputed from fields.
 *   - `ui-projection-deferred` — shown but has NO typed home for its display
 *                                value (Plot Style / Material — §9.2/edge-13);
 *                                a UI projection, NOT deletable, NOT writable.
 */
export type PropertyChannelClass =
  | "editable-typed-field"
  | "read-only-calc"
  | "ui-projection-deferred";

export type NumericConstraint = {
  /** value must be strictly greater than this (zod `.positive()` ⇒ 0). */
  exclusiveMin?: number;
  /** value must be ≥ this (zod `.nonnegative()` ⇒ 0). */
  min?: number;
  max?: number;
  /** value must be an integer (zod `.int()`) — S16 widget + S15 validation must
   *  round/reject fractional input (e.g. array Rows/Columns). */
  int?: boolean;
  /** display precision (decimals). Mirrors `formatNumber` (3); production keeps
   *  full precision per DEFUSE S0.3 budget. */
  precision?: number;
};

export type DescriptorConstraints = {
  numeric?: NumericConstraint;
  /** allowed values for an `enum` editor (integer codes as strings, or labels). */
  enumValues?: readonly string[];
  /** point-coord edit MUST re-emit the full point incl. Z so `patchEntityById`'s
   *  shallow merge does not drop a non-zero elevation (residual #317 Z-preserve;
   *  mirrors `reconcileLine` / the Circle/Arc withOptionalZ contract). */
  zPreserve?: boolean;
  /** numeric edit reconciles against the document at display precision before
   *  writing (mirrors the HATCH Scale/Angle + Circle/Arc doc-precision reconcile
   *  in `propertyTranslator`) so a full-precision value is not clobbered by a
   *  formatNumber-rounded re-dispatch of an unrelated field. */
  docPrecisionReconcile?: boolean;
  /** rendered in the palette but rejects edits at the patch boundary
   *  (block-record-sourced / behavior-backed metadata — BEDIT territory; or a
   *  UX-level always-read-only field like ArrayRect `Associative`, S16-
   *  ARRAYRECT-READONLY-ALIGN W2.1 — AutoCAD never lets Properties flip an
   *  existing array's associativity, only EXPLODE does). A plain `boolean`
   *  is entity-INDEPENDENT (every instance of the kind) — every descriptor
   *  in the registry uses this form today. The function form is reserved for
   *  a future entity-STATE-dependent gate (a field read-only only in some
   *  instances of a kind, not others); resolve EITHER form with
   *  {@link resolveReadOnly} — never read this field directly, a function
   *  reference is always truthy. */
  readOnly?: boolean | ((entity: DrawingEntity) => boolean);
  /** N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2) — a SHELL-SAFE companion
   *  to a FUNCTION-valued {@link readOnly}. The extended-geometry SHELL mirror
   *  (`typedExtendedGeometryShellRejects`, `extendedPropertyMutations.ts`)
   *  operates on the STRING preview bag, not the live `DrawingEntity` — it has
   *  no `entity` to call the real `readOnly` predicate against, so WITHOUT
   *  this it optimistically accepts every write into the bag and lets ONLY
   *  the document writer's step-1 `resolveReadOnly` reject it — a real
   *  shell↔document divergence (the bag/audit/MATCHPROP-copy-source show a
   *  value the persisted document never had). `readOnlyFromBag` re-derives
   *  the SAME per-instance verdict from sibling PREVIEW BAG STRINGS instead
   *  of the typed entity (e.g. Hatch `Double` reads the bag's already-decoded
   *  `"Pattern type"` label). Returns `undefined` when the bag lacks the
   *  needed sibling key (degrades to the pre-existing accept-and-let-the-
   *  writer-decide path — never fabricates a false reject on an
   *  incomplete/legacy bag); `true`/`false` when it can decide. Consulted by
   *  the shell mirror ONLY when `readOnly` is itself a function — a STATIC
   *  `readOnly: true` already excludes the label from "owned" (nothing
   *  reaches the shell mirror to begin with).
   *
   *  @codex P2 round 5 — the OPTIONAL second argument is the FULL raw
   *  `changes` batch (the SAME array {@link readOnlyFromChanges} receives),
   *  threaded so a descriptor can scan it ORDER-INDEPENDENTLY instead of
   *  relying on the bag param alone — the bag param, on its own, only ever
   *  reflects whichever SIBLING changes the shell's accumulator loop has
   *  ALREADY processed BEFORE reaching this label (`nextProperties`, order-
   *  DEPENDENT), while `readOnlyFromChanges` (the document twin) always scans
   *  the WHOLE batch (order-INDEPENDENT via `Array.find`). Without also
   *  threading `changes` here, a batch that lists the dependent label (Hatch
   *  "Double") BEFORE its sibling ("Pattern type") makes the SHELL decide
   *  against stale state while the DOCUMENT decides against the batch's
   *  final intent — the two diverge on exactly the ordering the bag-param
   *  alone cannot see. A descriptor should scan `changes` FIRST (when
   *  provided) for the sibling label and fall back to the bag param only
   *  when the sibling is absent from `changes` — the SAME priority order
   *  keeps single-field applies (no `changes` array in scope, e.g. a
   *  standalone MATCHPROP copy) working off the bag alone, unchanged. */
  readOnlyFromBag?: (
    bagProperties: Readonly<Record<string, string>>,
    changes?: ReadonlyArray<{ name: string; value: string }>,
  ) => boolean | undefined;
  /** N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2 round 3) — the DOCUMENT-
   *  level twin of {@link readOnlyFromBag}, for a FUNCTION-valued {@link
   *  readOnly} whose dependency is ANOTHER field in the SAME `properties/
   *  apply` batch that is NOT itself typed-writer-owned (e.g. Hatch "Pattern
   *  type", an `enum` — excluded from {@link TypedPropertyDescriptor}
   *  ownership, so it is applied by the SEPARATE legacy-translator step,
   *  which runs AFTER the typed writer's own accumulator loop
   *  (`buildTypedFieldPatch`). WITHOUT this, a single apply that changes
   *  BOTH "Pattern type" and "Double" evaluates "Double"'s `readOnly`
   *  against the entity's PRE-apply `patternType` — the sibling change
   *  hasn't landed yet from this loop's point of view — producing an
   *  ORDER-DEPENDENT verdict that can diverge from what the FINAL
   *  (post-apply) entity would actually license (@codex: "a Predefined→User
   *  defined + Double=Yes edit is rejected against the stale type, while
   *  the reverse transition can leave doubleLine=true on a final Predefined
   *  hatch"). Given the FULL batch `changes` (not just the bag) plus the
   *  CURRENT accumulator `entity`, returns a PARTIAL entity override
   *  reflecting the sibling's NEW value (e.g. `{ patternType: 0 }`) — used
   *  ONLY to evaluate `readOnly` for THIS edit; `undefined` when no
   *  relevant sibling change is present in this batch (defers to the
   *  entity's own current field, unchanged behaviour). The override NEVER
   *  leaks into the accumulator or the emitted patch — only the
   *  descriptor's OWN `typedFieldRef` top field is ever merged back
   *  (`buildTypedFieldPatch`, typedPropertyWriter.ts). */
  readOnlyFromChanges?: (
    changes: ReadonlyArray<{ name: string; value: string }>,
    entity: DrawingEntity,
  ) => Partial<DrawingEntity> | undefined;
  /** the write is NOT a plain typed-field set: it triggers dependent-field
   *  rebuilds, so S15 MUST route it through the named mutator rather than
   *  patching `typedFieldRef.path` directly (e.g. HATCH Pattern → solidFill /
   *  gradient / customPatternLines reset). Value = the mutator identifier. */
  sideEffects?: string;
};

/**
 * A typed accessor into the canonical `DrawingEntity`. `read` is the executable
 * resolution the completeness gate exercises against a schema-parsed fixture;
 * `path` is the human-readable dotted field path (empty for a computed accessor
 * with no single schema field — e.g. Multileader Arrow, derived from
 * `leaderRoots`). When `path` is non-empty the gate ALSO walks it and asserts it
 * agrees with `read`, catching a path↔accessor typo.
 */
export type TypedFieldRef = {
  path: string;
  read: (entity: DrawingEntity) => unknown;
};

export type TypedPropertyDescriptor = {
  /** canonical English property key — identical to today's string-bag key /
   *  `propertyDraft` key / `propertyCapabilities` label (the migration anchor). */
  label: string;
  /** derived i18n message key (`property.<snake>`), the SAME key
   *  `PropertyGrid.translatePropertyLabel` derives — referenced, never hardcoded
   *  as a RU/EN literal (i18n owns the strings, in apps/web). */
  messageKey: string;
  editorKind: EditorKind;
  channel: PropertyChannelClass;
  /** absent for `read-only-calc` with no computed accessor and for every
   *  `ui-projection-deferred` field (no typed home). */
  typedFieldRef?: TypedFieldRef;
  constraints?: DescriptorConstraints;
  /** the panel display STRING, mirroring the production single-select builder
   *  (`dxfGeometry.buildXxxPreview`) EXACTLY — S16 renders THIS, not the raw
   *  `typedFieldRef.read` value. Present ONLY where the display differs from the
   *  default projection (degree-projected angles, decoded labels, serialized
   *  arrays, derived scale). When absent, S16 applies the default projection for
   *  the editorKind (`formatNumber` for number/point-coord, Yes/No for boolean,
   *  String otherwise) — see `resolveDisplay`. */
  display?: (entity: DrawingEntity) => string;
  /** S15 write inverse — the panel display string the user typed → the raw typed
   *  value to patch `typedFieldRef.path`. Present where the display↔raw mapping
   *  is non-trivial (degrees→radians, serialized string→array); absent ⇒ either
   *  the identity numeric parse (`Number`) for a plain editable field, or a
   *  read-only row (decoded label / derived calc). MUST return a REJECT SENTINEL
   *  (`null`/`undefined`/non-finite) that S15 treats as a NO-OP for malformed
   *  input, never a corrupt raw value. The optional `entity` gives access to the
   *  live document entity for cross-field validation (e.g. Bulges count-lock
   *  against the vertex count). */
  toRaw?: (display: string, entity?: DrawingEntity) => unknown;
};

/** i18n key derivation — the EXACT algorithm `PropertyGrid.translatePropertyLabel`
 *  uses (`"property." + label.toLowerCase().replace(/ /g, "_")`), kept here so
 *  the descriptor carries the message-key reference without importing apps/web
 *  (forbidden dep edge) or hardcoding localized strings. */
export function propertyMessageKey(label: string): string {
  return "property." + label.toLowerCase().replace(/ /g, "_");
}

/**
 * Resolve `descriptor.constraints.readOnly` against a live `entity` — the ONE
 * place every consumer (the S15 write boundary, the S16 panel-read cutover,
 * the extended-geometry SHELL mirror) evaluates it, so a future entity-STATE
 * predicate and a plain entity-INDEPENDENT flag (BEDIT-territory metadata,
 * ArrayRect `Associative`) can never drift apart. A `boolean` ignores
 * `entity`; a function is called with it; `undefined` resolves to `false`
 * (not read-only). NEVER read `constraints.readOnly` directly elsewhere — a
 * function reference is always truthy, so a bare
 * `if (descriptor.constraints?.readOnly)` would silently treat a future
 * conditional predicate as an unconditional `true`.
 */
export function resolveReadOnly(
  descriptor: TypedPropertyDescriptor,
  entity: DrawingEntity,
): boolean {
  const readOnly = descriptor.constraints?.readOnly;
  return typeof readOnly === "function" ? readOnly(entity) : readOnly === true;
}

type FieldOpts = {
  channel?: PropertyChannelClass;
  constraints?: DescriptorConstraints;
  display?: (entity: DrawingEntity) => string;
  toRaw?: (display: string, entity?: DrawingEntity) => unknown;
};

/** Wrap a kind-specific accessor into the `DrawingEntity`-typed `read`. The lone
 *  union-narrowing cast lives here so per-kind tables stay one-liners. */
function ref<E extends DrawingEntity>(
  path: string,
  read: (entity: E) => unknown,
): TypedFieldRef {
  return { path, read: (entity) => read(entity as E) };
}

/** Low-level descriptor factory (auto-derives the message key). Exported so a
 *  per-kind table can spell out an editorKind the sugar helpers don't cover
 *  (e.g. `lineweight`, a read-only-but-homed field, a derived point-coord). */
export function make(
  label: string,
  editorKind: EditorKind,
  typedFieldRef: TypedFieldRef | undefined,
  opts: FieldOpts = {},
): TypedPropertyDescriptor {
  return {
    label,
    messageKey: propertyMessageKey(label),
    editorKind,
    channel: opts.channel ?? "editable-typed-field",
    ...(typedFieldRef ? { typedFieldRef } : {}),
    ...(opts.constraints ? { constraints: opts.constraints } : {}),
    ...(opts.display ? { display: opts.display } : {}),
    ...(opts.toRaw ? { toRaw: opts.toRaw } : {}),
  };
}

/* ── One-liner descriptor factories (kind-generic) ─────────────────────────── */

export function num<E extends DrawingEntity>(
  label: string,
  path: string,
  read: (e: E) => unknown,
  numeric?: NumericConstraint,
): TypedPropertyDescriptor {
  return make(label, "number", ref(path, read), {
    constraints: { numeric: { precision: 3, ...(numeric ?? {}) } },
  });
}

export function coord<E extends DrawingEntity>(
  label: string,
  path: string,
  read: (e: E) => unknown,
  constraints?: DescriptorConstraints,
): TypedPropertyDescriptor {
  return make(label, "point-coord", ref(path, read), {
    constraints: { numeric: { precision: 3 }, ...(constraints ?? {}) },
  });
}

export function txt<E extends DrawingEntity>(
  label: string,
  path: string,
  read: (e: E) => unknown,
  opts: FieldOpts = {},
): TypedPropertyDescriptor {
  return make(label, "text", ref(path, read), opts);
}

export function styleRef<E extends DrawingEntity>(
  label: string,
  path: string,
  read: (e: E) => unknown,
): TypedPropertyDescriptor {
  // style names (Text/Dimension/Multileader style) — a text editor today, but a
  // dedicated symbol-table ref; kept `text` editorKind so S16 dispatch is a pure
  // 1:1 with `PropertyGrid.isStyleProperty`.
  return make(label, "text", ref(path, read));
}

export function bool<E extends DrawingEntity>(
  label: string,
  path: string,
  read: (e: E) => unknown,
): TypedPropertyDescriptor {
  return make(label, "boolean", ref(path, read));
}

export function enumField<E extends DrawingEntity>(
  label: string,
  path: string,
  read: (e: E) => unknown,
  enumValues?: readonly string[],
): TypedPropertyDescriptor {
  return make(label, "enum", ref(path, read), {
    constraints: enumValues ? { enumValues } : undefined,
  });
}

/** read-only derived display value (Appendix B pureUiKeys). `read` (optional)
 *  returns the raw computed value (the gate asserts it resolves when present);
 *  `display` (optional) is the exact panel string (decoded label / derived
 *  scale) — supply it whenever the display differs from `formatNumber(read)`. */
export function calc<E extends DrawingEntity>(
  label: string,
  read?: (e: E) => unknown,
  display?: (e: E) => string,
): TypedPropertyDescriptor {
  return make(label, "read-only-calc", read ? ref("", read) : undefined, {
    channel: "read-only-calc",
    ...(display ? { display: (e) => display(e as E) } : {}),
  });
}

/** shown but with NO typed home for its display value — a UI projection, not
 *  deletable and not writable (Plot Style / Material — §9.2/edge-13). */
export function deferred(
  label: string,
  editorKind: EditorKind = "text",
): TypedPropertyDescriptor {
  return make(label, editorKind, undefined, {
    channel: "ui-projection-deferred",
    constraints: { readOnly: true },
  });
}

/** behavior-backed / block-record-sourced metadata: has a display value but its
 *  typed home is on a graph node (BlockDefinition / BLOCK_RECORD), not the
 *  entity — read-only at the INSERT boundary until BEDIT (S15 prerequisite). */
export function behaviorBacked(
  label: string,
  editorKind: EditorKind = "text",
): TypedPropertyDescriptor {
  return make(label, editorKind, undefined, {
    channel: "read-only-calc",
    constraints: { readOnly: true },
  });
}
